import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { FacilityIdSchema } from "./facility.contract.js";
import { ManualIdSchema, ManualSearchListSchema } from "./manual.contract.js";
/**
 * Favourites contract (ADR-011). A user's saved facilities AND manuals — the ONE dual-ready `favorite` table backs both.
 * Each list returns **ids only**; the client maps them onto its already-loaded tree/manuals and skips any it can't see
 * (a lost-access favourite must not surface — panel). Add/remove are idempotent toggles. Browser-safe (zod + ts-rest).
 */
const c = initContract();
const Unauthorized = z.object({ message: z.string() });
/** The caller's own favourite facility ids (already filtered to currently-visible facilities server-side). */
export const UserFavouritesSchema = z.object({
    facilities: z.array(z.number().int()),
});
/** The caller's own favourite manual ids (already filtered to currently-viewable manuals server-side). */
export const UserManualFavouritesSchema = z.object({
    manuals: z.array(z.number().int()),
});
export const favoriteContract = c.router({
    listUserFavourites: {
        method: "GET",
        path: "/user/favourites",
        responses: {
            200: UserFavouritesSchema,
            401: Unauthorized,
        },
        summary: "The caller's favourite facilities (ids), filtered to currently-visible facilities.",
    },
    addFacilityFavourite: {
        method: "POST",
        path: "/facilities/:id/favourite",
        pathParams: z.object({ id: FacilityIdSchema }),
        body: z.object({}).strict(),
        responses: {
            200: z.object({ favourited: z.literal(true) }),
            401: Unauthorized,
            403: z.object({ message: z.string(), code: z.literal("FACILITY_FORBIDDEN") }),
            404: z.object({ message: z.string(), code: z.literal("FACILITY_NOT_FOUND") }),
        },
        summary: "Add a facility to the caller's favourites (gated on facility visibility; idempotent).",
    },
    removeFacilityFavourite: {
        method: "DELETE",
        path: "/facilities/:id/favourite",
        pathParams: z.object({ id: FacilityIdSchema }),
        responses: {
            200: z.object({ favourited: z.literal(false) }),
            401: Unauthorized,
        },
        summary: "Remove a facility from the caller's favourites (idempotent).",
    },
    // ── Manual favourites (ADR-011 fast-follow — same dual-ready table). Mirror the facility endpoints: the list is
    // ids-only (client intersects with its loaded manuals), add is gated on manual VISIBILITY (the #107 direct-view
    // predicate — same gate as reading it), both toggles are idempotent.
    listUserManualFavourites: {
        method: "GET",
        path: "/user/manual-favourites",
        responses: {
            200: UserManualFavouritesSchema,
            401: Unauthorized,
        },
        summary: "The caller's favourite manuals (ids), filtered to currently-viewable manuals.",
    },
    addManualFavourite: {
        method: "POST",
        path: "/manuals/:id/favourite",
        pathParams: z.object({ id: ManualIdSchema }),
        body: z.object({}).strict(),
        responses: {
            200: z.object({ favourited: z.literal(true) }),
            401: Unauthorized,
            403: z.object({ message: z.string(), code: z.literal("MANUAL_FORBIDDEN") }),
            404: z.object({ message: z.string(), code: z.literal("MANUAL_NOT_FOUND") }),
        },
        summary: "Add a manual to the caller's favourites (gated on manual visibility; idempotent).",
    },
    removeManualFavourite: {
        method: "DELETE",
        path: "/manuals/:id/favourite",
        pathParams: z.object({ id: ManualIdSchema }),
        responses: {
            200: z.object({ favourited: z.literal(false) }),
            401: Unauthorized,
        },
        summary: "Remove a manual from the caller's favourites (idempotent).",
    },
    // The caller's favourite manuals as FULL cards (name + state + facility location) — powers the global "Manual
    // favourites" rail (the drill-down doesn't have the manuals loaded, unlike the per-facility star which uses the
    // ids-only list above). Same DTO + permission filter as `/manuals/search` (#107 direct-view, active manual on an
    // active facility); the ids-only list stays for the in-facility star state.
    listUserManualFavouritesDetailed: {
        method: "GET",
        path: "/user/manual-favourites/list",
        responses: {
            200: ManualSearchListSchema,
            401: Unauthorized,
        },
        summary: "The caller's favourite manuals as full cards (active + directly-viewable, with facility location).",
    },
});
//# sourceMappingURL=favorite.contract.js.map